import { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { brokerService } from '../config/api';

const Brokers = () => {
  const [onboardedBrokers, setOnboardedBrokers] = useState([]);
  const [supportedBrokers, setSupportedBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [apiCredentials, setApiCredentials] = useState({
    apiKey: '',
    secretKey: '',
    redirectUrl: '',
  });
  const [showCredentialsForm, setShowCredentialsForm] = useState(false);

  const [onboardingConfig, setOnboardingConfig] = useState(null);

  useEffect(() => {
    fetchBrokers();
  }, []);

  const fetchBrokers = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        // throw new Error('Authentication token not found');
      }

      // Fetch both onboarded and supported brokers
      const [onboardedResponse, supportedResponse] = await Promise.all([
        brokerService.getOnboardedBrokers(),
        brokerService.getSupportedBrokers()
      ]);

      setOnboardedBrokers(onboardedResponse.data);
      setSupportedBrokers(supportedResponse.data);
    } catch (err) {
      console.error('Broker fetch error:', err);
      setError(err.message || 'An error occurred while fetching brokers');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (brokerName) => {
    try {
      const response = await brokerService.connectBroker(brokerName.toLowerCase());
      // The API should return the login URL
      if (response.data && response.data.loginUrl) {
        // Redirect to the broker's login page
        window.location.href = response.data.loginUrl;
      }
    } catch (error) {
      console.error('Error connecting to broker:', error);
      // You might want to add error handling here (e.g., showing a snackbar)
    }
  };

  const handleOnboard = async (brokerId) => {
    try {
      setLoading(true);
      const response = await brokerService.getBrokerOnboardingConfig(brokerId);
      const config = response.data;

      setOnboardingConfig(config);

      // Pre-fill form with config data
      setApiCredentials({
        apiKey: config.apiKey || '',
        secretKey: config.secretKey || '',
        redirectUrl: config.redirectUrl || '',
        appName: config.appName || '',
      });

      // Find the broker object to set as selected
      const broker = supportedBrokers.find(b => b.id === brokerId) || onboardedBrokers.find(b => b.id === brokerId);
      setSelectedBroker(broker);

      setConnectDialogOpen(true);
      setShowCredentialsForm(true);
    } catch (error) {
      console.error('Error fetching broker config:', error);
      setError('Failed to initialize onboarding. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setConnectDialogOpen(false);
    setShowCredentialsForm(false);
    setOnboardingConfig(null);
    setApiCredentials({ apiKey: '', secretKey: '', redirectUrl: '', appName: '' });
  };

  const handleBeginOnboarding = () => {
    setShowCredentialsForm(true);
  };

  const handleCredentialsSubmit = async () => {
    try {
      setLoading(true);

      // Merge form data with original config to ensure all required fields (like id, platform) are present
      const requestBody = {
        ...onboardingConfig,
        apiKey: apiCredentials.apiKey,
        secretKey: apiCredentials.secretKey,
        appName: apiCredentials.appName,
        // redirectUrl is taken from config/state but not shown in form
        redirectUrl: apiCredentials.redirectUrl || onboardingConfig.redirectUrl
      };

      await brokerService.onboardBrokerManual(onboardingConfig.brokerId, requestBody);

      // Refresh list after successful onboarding
      await fetchBrokers();
      handleCloseDialog();

    } catch (err) {
      console.error('Broker onboarding error:', err);
      setError(err.message || 'An error occurred while onboarding broker');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async (broker) => {
    if (!window.confirm(`Are you sure you want to remove ${broker.name}?`)) {
      return;
    }

    try {
      setLoading(true);
      await brokerService.removeBroker(broker.id);
      await fetchBrokers(); // Refresh the list
    } catch (err) {
      console.error('Error removing broker:', err);
      setError(err.message || 'Failed to remove broker');
      setLoading(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setApiCredentials(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const renderBrokerGrid = (brokers, isOnboarded = false) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {brokers.map((broker) => (
        <div key={broker.id} className="card bg-base-100 shadow-xl hover:shadow-2xl transition-shadow">
          <div className="card-body">
            <div className="flex items-center gap-4 mb-4">
              {broker.logo ? (
                <img
                  src={broker.logo}
                  alt={`${broker.name} logo`}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                  className="w-12 h-12 object-contain"
                />
              ) : null}
              <div 
                className="w-12 h-12 rounded-full bg-primary text-primary-content flex items-center justify-center text-xl font-bold"
                style={{ display: broker.logo ? 'none' : 'flex' }}
              >
                {broker.name?.[0]?.toUpperCase() || 'B'}
              </div>
              <div className="flex-1">
                <h2 className="card-title text-base-content">{broker.name}</h2>
                {isOnboarded && (
                  <span 
                    className={`badge ${broker.status === 'connected' ? 'badge-success' : 'badge-error'} gap-1 cursor-pointer`}
                    onClick={() => broker.status !== 'connected' && handleConnect(broker.name.toLowerCase())}
                  >
                    {broker.status === 'connected' ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    {broker.status === 'connected' ? "Connected" : "Disconnected"}
                  </span>
                )}
              </div>
            </div>

            <p className="text-base-content/70 mb-4">{broker.description}</p>

            <div className="flex flex-wrap gap-2 mb-4">
              {broker.features?.map((feature) => (
                <span key={feature} className="badge badge-outline badge-sm">
                  {feature}
                </span>
              ))}
            </div>

            <div className="card-actions justify-end mt-auto">
              {isOnboarded ? (
                <button
                  className="btn btn-outline btn-error w-full"
                  onClick={() => handleDisconnect(broker)}
                >
                  Remove Broker
                </button>
              ) : (
                <button
                  className="btn btn-primary w-full"
                  onClick={() => handleOnboard(broker.id)}
                >
                  Onboard Broker
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-[50vh]">
          <span className="loading loading-spinner loading-lg text-primary"></span>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6">
        {error && (
          <div className="alert alert-error mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
            <button className="btn btn-sm btn-ghost" onClick={fetchBrokers}>
              Retry
            </button>
          </div>
        )}

        {/* Onboarded Brokers Section */}
        <div className="mb-12">
          <h1 className="text-3xl font-bold text-base-content mb-2">Your Onboarded Brokers</h1>
          <p className="text-base-content/70 mb-6">Manage your onboarded trading accounts</p>

          {onboardedBrokers.length > 0 ? (
            renderBrokerGrid(onboardedBrokers, true)
          ) : (
            <div className="alert alert-info">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <span>You haven't onboarded any brokers yet. Onboard a broker to start trading.</span>
            </div>
          )}
        </div>

        {/* Available Brokers Section */}
        <div className="mt-12">
          <h1 className="text-3xl font-bold text-base-content mb-2">Available Brokers</h1>
          <p className="text-base-content/70 mb-6">Onboard additional brokers to expand your trading capabilities</p>

          {renderBrokerGrid(supportedBrokers)}
        </div>

        {/* Onboard Dialog */}
        <dialog className={`modal ${connectDialogOpen ? 'modal-open' : ''}`}>
          <div className="modal-box">
            <h3 className="font-bold text-lg text-base-content">Onboard {selectedBroker?.name}</h3>
            <p className="py-4 text-base-content/70">
              Please enter your {selectedBroker?.name} API credentials
            </p>
            
            <div className="space-y-4">
              <div className="fieldset w-full">
                <label className="label">
                  <span className="text-sm">App Name</span>
                </label>
                <input
                  type="text"
                  name="appName"
                  placeholder="Enter app name"
                  className="input input w-full"
                  value={apiCredentials.appName || ''}
                  onChange={handleInputChange}
                  required
                />
                <label className="label">
                  <span className="text-xs text-base-content/60 text-base-content/50">Enter the App Name from your broker's dashboard</span>
                </label>
              </div>

              <div className="fieldset w-full">
                <label className="label">
                  <span className="text-sm">API Key</span>
                </label>
                <input
                  type="text"
                  name="apiKey"
                  placeholder="Enter API key"
                  className="input input w-full"
                  value={apiCredentials.apiKey}
                  onChange={handleInputChange}
                  required
                />
                <label className="label">
                  <span className="text-xs text-base-content/60 text-base-content/50">Enter the API key from your broker's dashboard</span>
                </label>
              </div>

              <div className="fieldset w-full">
                <label className="label">
                  <span className="text-sm">Secret Key</span>
                </label>
                <input
                  type="password"
                  name="secretKey"
                  placeholder="Enter secret key"
                  className="input input w-full"
                  value={apiCredentials.secretKey}
                  onChange={handleInputChange}
                  required
                />
                <label className="label">
                  <span className="text-xs text-base-content/60 text-base-content/50">Enter the secret key from your broker's dashboard</span>
                </label>
              </div>
            </div>

            <div className="modal-action">
              <button className="btn btn-ghost" onClick={handleCloseDialog}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCredentialsSubmit}
                disabled={!apiCredentials.apiKey || !apiCredentials.secretKey || !apiCredentials.appName}
              >
                Submit
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button onClick={handleCloseDialog}>close</button>
          </form>
        </dialog>
      </div>
    </DashboardLayout>
  );
};

export default Brokers;